// src/app/AppShell.jsx
import React, { useMemo, useState, useEffect, useRef } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Animated, ScrollView, TextInput, Keyboard, Image } from 'react-native';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { FaPlus, FaStickyNote, FaBars, FaTimes } from 'react-icons/fa';
import axios from 'axios';
import API_BASE_URL from '../apiConfig.js';

// Set up axios interceptor for handling expired tokens globally
let isInterceptorSetup = false;
let navigateRef = null;
let setIsAuthenticatedRef = null;

function setupAxiosInterceptor() {
  if (isInterceptorSetup) return;
  isInterceptorSetup = true;

  axios.interceptors.response.use(
    response => response,
    async error => {
      if (error.response?.status === 401) {
        const errorDetail = error.response?.data?.detail || '';
        if (errorDetail.includes("Token has expired") || errorDetail.includes("expired")) {
          // Clear storage
          localStorage.removeItem('userToken');
          localStorage.removeItem('userPermissions');
          localStorage.removeItem('activeJobs');
          localStorage.removeItem('activeInvoices');
          localStorage.removeItem('activeCustomers');
          
          alert("Your session has expired. Please log in again.");
          
          // Update authentication state and redirect
          if (setIsAuthenticatedRef) {
            setIsAuthenticatedRef(false);
          }
          if (navigateRef) {
            navigateRef('/login', { replace: true });
          }
        }
      }
      return Promise.reject(error);
    }
  );
}

// Mobile detection hook
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIfMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
      const isSmallScreen = window.innerWidth <= 768;
      setIsMobile(isMobileDevice || isSmallScreen);
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  return isMobile;
}

function usePageActions() {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  return useMemo(() => {
    // Home page
    if (pathname === '/' || pathname === '/index') {
      return { addLabel: null, onAdd: null, showNotes: true };
    }
    // Decide what the Add button should do per-page
    if (pathname.startsWith('/view-customers')) {
      return { addLabel: null, onAdd: null, showNotes: true };
    }
    if (pathname.startsWith('/schedule')) {
      return { addLabel: null, onAdd: null, showNotes: true };
    }
    if (pathname.startsWith('/jobs')) {
      return { addLabel: 'Add Job', onAdd: () => navigate('/new-job'), showNotes: true };
    }
    if (pathname.startsWith('/job-details')) {
      return { addLabel: null, onAdd: null, showNotes: true };
    }
    if (pathname.startsWith('/quotes')) {
      return { addLabel: 'Add Quote', onAdd: () => navigate('/create-quote'), showNotes: true };
    }
    if (pathname.startsWith('/quote-details')) {
      return { addLabel: null, onAdd: null, showNotes: true };
    }
    if (pathname.startsWith('/invoices')) {
      return { addLabel: 'Add Invoice', onAdd: () => navigate('/create-invoice'), showNotes: true };
    }
    if (pathname.startsWith('/invoice-details')) {
      return { addLabel: null, onAdd: null, showNotes: true };
    }
    if (pathname.startsWith('/customer-details')) {
      return { addLabel: null, onAdd: null, showNotes: true };
    }
    if (pathname.startsWith('/customer-jobs')) {
      return { addLabel: null, onAdd: null, showNotes: true };
    }
    if (pathname.startsWith('/customer-invoices')) {
      return { addLabel: null, onAdd: null, showNotes: true };
    }
    if (pathname.startsWith('/customer-quotes')) {
      return { addLabel: null, onAdd: null, showNotes: true };
    }
    if (pathname.startsWith('/settings')) {
      return { addLabel: null, onAdd: null, showNotes: true };
    }
    // Other pages
    return { addLabel: null, onAdd: null, showNotes: false };
  }, [pathname, navigate]);
}

export default function AppShell({ setIsAuthenticated }) {
  const { addLabel, onAdd, showNotes } = usePageActions();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [menuVisible, setMenuVisible] = useState(false);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [notes, setNotes] = useState('');
  const [isNotesVisible, setIsNotesVisible] = useState(false);
  const notesAnimation = useRef(new Animated.Value(0)).current;
  const mobileMenuAnimation = useRef(new Animated.Value(0)).current;
  const scrollViewRef = useRef(null);
  const [userPermissions, setUserPermissions] = useState(null);
  const [businessLogo, setBusinessLogo] = useState(null);

  // Check if we're on the confirm-account page
  const isConfirmAccountPage = location.pathname === '/confirm-account';
  
  // Check if we're on the create-account page
  const isCreateAccountPage = location.pathname === '/create-account';

  // Load user permissions on component mount
  useEffect(() => {
    const loadUserPermissions = async () => {
      try {
        const permissions = localStorage.getItem('userPermissions');
        if (permissions) {
          setUserPermissions(JSON.parse(permissions));
        }
      } catch (error) {
        console.error('Error loading user permissions:', error);
      }
    };
    loadUserPermissions();
  }, []);

  // Fetch business logo
  useEffect(() => {
    const fetchBusinessLogo = async () => {
      try {
        const token = localStorage.getItem('userToken');
        if (!token) return;

        const response = await axios.get(`${API_BASE_URL}/api/settings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        if (response.data?.businessLogo) {
          setBusinessLogo(response.data.businessLogo);
        }
      } catch (error) {
        console.error('Error fetching business logo:', error);
      }
    };

    if (!isConfirmAccountPage && !isCreateAccountPage) {
      fetchBusinessLogo();
    }
  }, [isConfirmAccountPage, isCreateAccountPage]);

  // Check if user has job permissions
  const hasJobPermissions = () => {
    if (!userPermissions) return false;
    const user_type = userPermissions.user_type;
    // Allow if user is Owner or Admin, or has job permission
    return user_type === 'owner' || user_type === 'admin' || userPermissions.job_perm !== 'none';
  };

  // Check if user has customer permissions
  const hasCustomerPermissions = () => {
    if (!userPermissions) return false;
    const user_type = userPermissions.user_type;
    // Allow if user is Owner or Admin, or has customer permission
    return user_type === 'owner' || user_type === 'admin' || userPermissions.customer_perm !== 'none';
  };

  // Check if user has invoice permissions
  const hasInvoicePermissions = () => {
    if (!userPermissions) return false;
    const user_type = userPermissions.user_type;
    // Allow if user is Owner or Admin, or has invoice permission
    return user_type === 'owner' || user_type === 'admin' || userPermissions.invoice_perm !== 'none';
  };

  // Check if user has quote permissions
  const hasQuotePermissions = () => {
    if (!userPermissions) return false;
    const user_type = userPermissions.user_type;
    // Allow if user is Owner or Admin, or has quote permission
    return user_type === 'owner' || user_type === 'admin' || userPermissions.quote_perm !== 'none';
  };

  // Check if user has quote write permissions
  const hasQuoteWritePermissions = () => {
    if (!userPermissions) return false;
    const user_type = userPermissions.user_type;
    // Allow if user is Owner or Admin, or has quote write permission
    return user_type === 'owner' || user_type === 'admin' || userPermissions.quote_perm === 'write' || userPermissions.quote_perm === undefined;
  };

  // Handle navigation with permission check for job-related pages
  const handleJobNavigation = (path) => {
    if (!hasJobPermissions()) {
      alert("You don't have permission to view jobs. Please contact your account administrator.");
      return;
    }
    setMenuVisible(false);
    setMobileMenuVisible(false);
    navigate(path);
  };

  // Handle navigation with permission check for customer-related pages
  const handleCustomerNavigation = (path) => {
    if (!hasCustomerPermissions()) {
      alert("You don't have permission to view customers. Please contact your account administrator.");
      return;
    }
    setMenuVisible(false);
    setMobileMenuVisible(false);
    navigate(path);
  };

  // Handle navigation with permission check for invoice-related pages
  const handleInvoiceNavigation = (path) => {
    if (!hasInvoicePermissions()) {
      alert("You don't have permission to view invoices. Please contact your account administrator.");
      return;
    }
    setMenuVisible(false);
    setMobileMenuVisible(false);
    navigate(path);
  };

  // Handle navigation with permission check for quote-related pages
  const handleQuoteNavigation = (path) => {
    if (!hasQuotePermissions()) {
      alert("You don't have permission to view quotes. Please contact your account administrator.");
      return;
    }
    
    // Check for create-quote specifically
    if (path === '/create-quote' && !hasQuoteWritePermissions()) {
      alert("You have read-only access to quotes and cannot create new quotes.");
      return;
    }
    
    setMenuVisible(false);
    setMobileMenuVisible(false);
    navigate(path);
  };

  useEffect(() => {
    const loadNotes = async () => {
      try {
        const savedNotes = localStorage.getItem('userNotes');
        if (savedNotes) {
          setNotes(savedNotes);
        }
      } catch (error) {
        console.error('Error loading notes:', error);
      }
    };

    loadNotes();
  }, []);

  const saveNotes = async (text) => {
    try {
      setNotes(text);
      localStorage.setItem('userNotes', text);
    } catch (error) {
      console.error('Error saving notes:', error);
    }
  };

  const toggleMenu = () => {
    setMenuVisible(!menuVisible);
  };

  const toggleMobileMenu = () => {
    const newState = !mobileMenuVisible;
    setMobileMenuVisible(newState);
    
    Animated.timing(mobileMenuAnimation, {
      toValue: newState ? 1 : 0,
      duration: 300,
      useNativeDriver: false,
    }).start();
  };

  const handleNavigation = (path) => {
    setMenuVisible(false);
    setMobileMenuVisible(false);
    navigate(path);
  };

  const handleSignOut = async () => {
    try {
      // Clear stored authentication data
      localStorage.removeItem('userToken');
      localStorage.removeItem('userPermissions');
      localStorage.removeItem('activeJobs');
      localStorage.removeItem('activeInvoices');
      localStorage.removeItem('activeCustomers');
      
      // Close mobile menu before signing out
      setMobileMenuVisible(false);
      
      // Update authentication state
      setIsAuthenticated(false);
    } catch (error) {
      console.error('Error during sign out:', error);
    }
  };

  const toggleNotes = () => {
    if (isNotesVisible) {
      Keyboard.dismiss();
      Animated.timing(notesAnimation, {
        toValue: 0,
        duration: 300,
        useNativeDriver: false,
      }).start(() => setIsNotesVisible(false));
    } else {
      setIsNotesVisible(true);
      Animated.timing(notesAnimation, {
        toValue: 1,
        duration: 300,
        useNativeDriver: false,
      }).start();
    }
  };

  const notesTranslateX = notesAnimation.interpolate({
    inputRange: [0, 1],
    outputRange: ['100%', '0%'],
  });

  const mobileMenuTranslateX = mobileMenuAnimation.interpolate({
    inputRange: [0, 1],
    outputRange: ['-100%', '0%'],
  });

  // Function to close mobile menu with animation
  const closeMobileMenu = () => {
    setMobileMenuVisible(false);
    Animated.timing(mobileMenuAnimation, {
      toValue: 0,
      duration: 300,
      useNativeDriver: false,
    }).start();
  };

  // Menu items component for reusability - Use toggleMobileMenu like the X button
  const MenuItems = ({ onItemPress }) => (
    <>
      <TouchableOpacity style={styles.menuItem} onPress={() => {
        toggleMobileMenu(); // Same function as the X button
        handleNavigation('/');
      }}>
        <Text style={styles.menuItemText}>🏠 Home</Text>
      </TouchableOpacity>
      <TouchableOpacity style={styles.menuItem} onPress={() => {
        toggleMobileMenu(); // Same function as the X button
        handleJobNavigation('/schedule');
      }}>
        <Text style={styles.menuItemText}>📅 Schedule</Text>
      </TouchableOpacity>
      <TouchableOpacity style={styles.menuItem} onPress={() => {
        toggleMobileMenu(); // Same function as the X button
        handleCustomerNavigation('/view-customers');
      }}>
        <Text style={styles.menuItemText}>👥 Customers</Text>
      </TouchableOpacity>
      <TouchableOpacity style={styles.menuItem} onPress={() => {
        toggleMobileMenu(); // Same function as the X button
        handleJobNavigation('/jobs');
      }}>
        <Text style={styles.menuItemText}>🛠️ Jobs</Text>
      </TouchableOpacity>
      <TouchableOpacity style={styles.menuItem} onPress={() => {
        toggleMobileMenu(); // Same function as the X button
        handleInvoiceNavigation('/invoices');
      }}>
        <Text style={styles.menuItemText}>💵 Invoices</Text>
      </TouchableOpacity>
      <TouchableOpacity style={styles.menuItem} onPress={() => {
        toggleMobileMenu(); // Same function as the X button
        handleQuoteNavigation('/quotes');
      }}>
        <Text style={styles.menuItemText}>📄 Quotes</Text>
      </TouchableOpacity>
      <TouchableOpacity style={styles.menuItem} onPress={() => {
        toggleMobileMenu(); // Same function as the X button
        handleNavigation('/settings');
      }}>
        <Text style={styles.menuItemText}>⚙️ Settings</Text>
      </TouchableOpacity>
      <TouchableOpacity style={styles.menuItem} onPress={() => {
        toggleMobileMenu(); // Same function as the X button
        handleSignOut();
      }}>
        <Text style={styles.menuItemText}>🚪 Sign Out</Text>
      </TouchableOpacity>
    </>
  );

  // Set up refs for the interceptor
  useEffect(() => {
    navigateRef = navigate;
    setIsAuthenticatedRef = setIsAuthenticated;
    setupAxiosInterceptor();
    
    return () => {
      navigateRef = null;
      setIsAuthenticatedRef = null;
    };
  }, [navigate, setIsAuthenticated]);

  return (
    <View style={styles.shell}>
      {/* Header */}
      {!isCreateAccountPage && (
        <View style={isMobile ? styles.headerMobile : styles.headerFullWidth}>
          {isMobile && !isConfirmAccountPage && (
            <TouchableOpacity style={styles.hamburgerButton} onPress={toggleMobileMenu}>
              <FaBars size={20} color="#003366" />
            </TouchableOpacity>
          )}
          {!isMobile && businessLogo && !isConfirmAccountPage && (
            <Image 
              source={{ uri: `${API_BASE_URL}/api/uploads/${businessLogo}` }} 
              style={styles.businessLogo}
            />
          )}
          <Text style={[styles.title, isMobile && styles.titleMobile]}>
            {isConfirmAccountPage ? 'Account Confirmation' : 'My Dashboard'}
          </Text>
        </View>
      )}

      {/* Desktop Sidebar - Hidden on mobile */}
      {!isConfirmAccountPage && !isCreateAccountPage && !isMobile && (
        <View style={styles.sidebarFullWidth}>
          <MenuItems onItemPress={handleNavigation} />
        </View>
      )}

      {/* Mobile Sidebar Overlay */}
      {!isConfirmAccountPage && !isCreateAccountPage && isMobile && (
        <>
          {mobileMenuVisible && (
            <TouchableOpacity 
              style={styles.mobileOverlay} 
              onPress={toggleMobileMenu}
              activeOpacity={1}
            />
          )}
          <Animated.View 
            style={[
              styles.mobileSidebar,
              { transform: [{ translateX: mobileMenuTranslateX }] }
            ]}
          >
            <View style={styles.mobileSidebarHeader}>
              <Text style={styles.mobileSidebarTitle}>Menu</Text>
              <TouchableOpacity onPress={toggleMobileMenu}>
                <FaTimes size={20} color="#003366" />
              </TouchableOpacity>
            </View>
            <MenuItems onItemPress={() => setMobileMenuVisible(false)} />
          </Animated.View>
        </>
      )}

      {/* Main Content */}
      <View style={[
        isConfirmAccountPage || isCreateAccountPage ? styles.contentFullWidth : 
        isMobile ? styles.contentMobile : styles.content
      ]}>
        <Outlet />
      </View>

      {/* Global actions - Adjusted for mobile */}
      {onAdd && (
        <TouchableOpacity style={[styles.fab, isMobile && styles.fabMobile]} onPress={onAdd}>
          <FaPlus size={16} color="#fff" />
          {!isMobile && <Text style={styles.fabText}>{addLabel}</Text>}
        </TouchableOpacity>
      )}
      {showNotes && !isCreateAccountPage && (
        <TouchableOpacity
          style={[styles.fab, styles.fabSecondary, isMobile && styles.fabSecondaryMobile]}
          onPress={toggleNotes}
        >
          <FaStickyNote size={16} color="#fff" />
          {!isMobile && <Text style={styles.fabText}>Notes</Text>}
        </TouchableOpacity>
      )}

      {/* Notes Panel - Adjusted for mobile */}
      <Animated.View
        style={[
          styles.notesPanel,
          isMobile && styles.notesPanelMobile,
          { transform: [{ translateX: notesTranslateX }] }
        ]}
      >
        <View style={styles.notesHeader}>
          <Text style={styles.notesHeaderText}>Notes:</Text>
          <TouchableOpacity style={styles.notesCloseButton} onPress={toggleNotes}>
            <Text style={styles.notesCloseText}>X</Text>
          </TouchableOpacity>
        </View>
        <ScrollView
          style={styles.notesScrollView}
          contentContainerStyle={{ flexGrow: 1 }}
          keyboardShouldPersistTaps="handled"
        >
          <TextInput
            style={styles.notesInput}
            multiline
            value={notes}
            onChangeText={saveNotes}
            placeholder="Type your notes here..."
          />
        </ScrollView>
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  shell: {
    minHeight: '100vh',
    backgroundColor: '#fff',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden', // Prevent horizontal scrolling
  },
  header: {
    height: 56,
    paddingHorizontal: 16,
    justifyContent: 'space-between',
    alignItems: 'center',
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    backgroundColor: '#f9fbff',
  },
  headerFullWidth: {
    height: 56,
    paddingHorizontal: 16,
    justifyContent: 'center',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    backgroundColor: '#f9fbff',
    width: '100%',
    flexDirection: 'row',
    position: 'relative',
  },
  headerMobile: {
    height: 56,
    paddingHorizontal: 16,
    justifyContent: 'flex-start',
    alignItems: 'center',
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    backgroundColor: '#f9fbff',
    width: '100%',
  },
  title: { fontSize: 18, fontWeight: '700', color: '#003366' },
  titleMobile: { marginLeft: 16 },
  hamburgerButton: { padding: 8 },
  sidebar: {
    width: 200,
    backgroundColor: '#f9fbff',
    borderRightWidth: 1,
    borderRightColor: '#eee',
    paddingVertical: 16,
  },
  sidebarFullWidth: {
    width: 200,
    backgroundColor: '#f9fbff',
    borderRightWidth: 1,
    borderRightColor: '#eee',
    paddingVertical: 16,
    position: 'fixed',
    top: 56, // Adjust for header height
    left: 0,
    bottom: 0,
  },
  mobileOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 9,
  },
  mobileSidebar: {
    position: 'fixed',
    top: 0,
    left: 0,
    bottom: 0,
    width: '80%',
    backgroundColor: '#f9fbff',
    zIndex: 10,
    paddingVertical: 16,
    paddingHorizontal: 8,
    borderRightWidth: 1,
    borderRightColor: '#eee',
  },
  mobileSidebarHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  mobileSidebarTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#003366',
  },
  menuItem: {
    padding: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  menuItemText: {
    fontSize: 16,
    color: '#003366',
  },
  content: {
    flex: 1,
    marginLeft: 200, // Adjust for sidebar width
    padding: 16,
    overflowY: 'auto', // Ensure content is scrollable vertically only
    overflowX: 'hidden', // Prevent horizontal scrolling in content
  },
  contentFullWidth: {
    flex: 1,
    padding: 16,
    overflowY: 'auto', // Ensure content is scrollable vertically only
    overflowX: 'hidden', // Prevent horizontal scrolling in content
  },
  contentMobile: {
    flex: 1,
    padding: 16,
    overflowY: 'auto', // Ensure content is scrollable vertically only
    overflowX: 'hidden', // Prevent horizontal scrolling in content
  },
  fab: {
    position: 'fixed',
    right: 16,
    bottom: 16,
    backgroundColor: '#007AFF',
    borderRadius: 999,
    paddingVertical: 10,
    paddingHorizontal: 14,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
  fabMobile: {
    paddingVertical: 10,
    paddingHorizontal: 10,
  },
  fabSecondary: {
    position: 'fixed',
    right: 16,
    bottom: 72, // stack above the main Add fab
    backgroundColor: '#007AFF',
    borderRadius: 999,
    paddingVertical: 10,
    paddingHorizontal: 14,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
  fabSecondaryMobile: {
    paddingVertical: 10,
    paddingHorizontal: 10,
  },
  fabText: { color: '#fff', fontWeight: '700', marginLeft: 8 },
  notesPanel: {
    position: 'absolute',
    top: '15%',
    right: 0,
    width: '90%',
    height: 200,
    backgroundColor: '#eee',
    borderLeftWidth: 1,
    borderColor: '#ccc',
    zIndex: 10,
  },
  notesPanelMobile: {
    width: '100%',
    height: '50%',
  },
  notesHeader: {
    backgroundColor: 'lightblue',
    padding: 5,
    borderTopLeftRadius: 5,
    borderTopRightRadius: 5,
  },
  notesHeaderText: {
    fontWeight: 'bold',
    marginBottom: 5,
    color: '#000',
  },
  notesCloseButton: {
    position: 'absolute',
    top: 0,
    right: 0,
    backgroundColor: 'lightblue',
    width: 30,
    height: 30,
    justifyContent: 'center',
    alignItems: 'center',
  },
  notesCloseText: {
    color: '#000',
    fontWeight: 'bold',
  },
  notesScrollView: {
    flex: 1,
    padding: 5,
  },
  notesInput: {
    flex: 1,
    fontSize: 16,
    textAlignVertical: 'top',
    padding: 10,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 5,
    minHeight: '100%',
  },
  businessLogo: {
    position: 'absolute',
    left: 16,
    width: 200,
    height: 40,
    borderRadius: 5,
  },
});
